import { useEffect } from 'react';
import type { FC } from 'react';
import NProgress from 'nprogress';
import LogoScreen from "src/assets/images/logo/isflogo-transparent.png"

const LoadingScreen: FC = () => {
  useEffect(() => {
    NProgress.start();

    return (): void => {
      NProgress.done();
    };
  }, []);

  return (
    <div 
      className="loading-screen-suspense flex items-center justify-center relative overflow-hidden"
      style={{ minHeight: '100vh', width: '100%' }}
    >
      {/* Dark elegant background with animated gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-950">
        {/* Animated gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }}></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-emerald-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }}></div>
        
        {/* Moving gradient overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-emerald-600/10"
          style={{
            animation: 'gradientShift 8s ease-in-out infinite',
            backgroundSize: '200% 200%',
          }}
        />
        
        {/* Shimmer effect */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
          style={{
            animation: 'shimmer 4s infinite',
            transform: 'translateX(-100%)',
          }}
        />
      </div>
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `floatParticle ${3 + Math.random() * 4}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo container with multiple glow effects */}
        <div className="relative mb-8">
          {/* Outer glow rings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="absolute w-64 h-64 border-2 border-emerald-500/20 rounded-full"
              style={{
                animation: 'pulseRing 2s ease-out infinite',
              }}
            />
            <div 
              className="absolute w-80 h-80 border border-blue-500/15 rounded-full"
              style={{
                animation: 'pulseRing 2.5s ease-out infinite',
                animationDelay: '0.5s',
              }}
            />
          </div>
          
          {/* Inner glow effect */}
          <div 
            className="absolute inset-0 bg-gradient-to-br from-emerald-400/40 via-blue-500/30 to-purple-500/40 rounded-full blur-2xl"
            style={{
              width: '220px',
              height: '220px',
              margin: '0 auto',
              transform: 'translate(50%, 50%)',
              marginLeft: '-110px',
              marginTop: '-110px',
              animation: 'glowPulse 3s ease-in-out infinite',
            }}
          />
          
          {/* Logo with floating animation */}
          <div
            style={{
              animation: 'logoFloat 3s ease-in-out infinite',
            }}
          >
            <img 
              src={LogoScreen} 
              alt="loading"
              className="w-48 relative z-10 drop-shadow-2xl filter brightness-110"
            />
          </div>
        </div>
        
        {/* Loading indicator with modern design */}
        <div className="flex flex-col items-center gap-4">
          {/* Progress bar */}
          <div className="w-48 h-1 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 rounded-full"
              style={{
                animation: 'progressBar 2s ease-in-out infinite',
              }}
            />
          </div>
          
          {/* Loading text */}
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm font-medium">Cargando</span>
            <div className="flex gap-1">
              <div 
                className="w-1.5 h-1.5 bg-emerald-400 rounded-full"
                style={{
                  animation: 'dotBounce 1.4s infinite ease-in-out',
                  animationDelay: '0s',
                }}
              />
              <div 
                className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                style={{
                  animation: 'dotBounce 1.4s infinite ease-in-out',
                  animationDelay: '0.2s',
                }}
              />
              <div 
                className="w-1.5 h-1.5 bg-purple-400 rounded-full"
                style={{
                  animation: 'dotBounce 1.4s infinite ease-in-out',
                  animationDelay: '0.4s',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%) skewX(-15deg);
          }
          100% {
            transform: translateX(200%) skewX(-15deg);
          }
        }
        
        @keyframes gradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes logoFloat {
          0%, 100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-12px) scale(1.02);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.4;
            transform: translate(50%, 50%) scale(1);
          }
          50% {
            opacity: 0.6;
            transform: translate(50%, 50%) scale(1.1);
          }
        }
        
        @keyframes pulseRing {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }
        
        @keyframes progressBar {
          0% {
            width: 0%;
            transform: translateX(0);
          }
          50% {
            width: 70%;
            transform: translateX(0);
          }
          100% {
            width: 100%;
            transform: translateX(0);
          }
        }
        
        @keyframes dotBounce {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          40% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
        
        @keyframes floatParticle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
